import {
  PREMIUM_TIERS,
  ALA_CARTE,
  COMPASSION_PLAN,
  SLIDING_SCALE_TIERS,
  BOUNDARIES,
  POSITIONING,
} from "@/lib/fsap-data";

/**
 * Automatic versioning for the downloadable FSAP retainer comparison PDF.
 *
 * The version is derived from the pricing/content payload itself, so any change
 * to tiers, à la carte pricing, the compassion plan, sliding scale, boundaries,
 * positioning, or the CTA link produces a new version string and a new
 * filename — no manual bump required. Readers holding a shared copy can compare
 * the stamped version against the live one on the pricing page.
 *
 * Outbound links use permanent paths backed by redirect routes in App.tsx, so
 * even stale copies always land on a live page.
 */

/** Permanent CTA path. Never remove this route — old PDFs point at it forever. */
export const RETAINER_PDF_CTA_PATH = "/advocacy/intake";

/** Canonical page that always serves the freshest pricing + newest PDF. */
export const RETAINER_PDF_LATEST_PATH = "/advocacy/pricing";

/** Legacy paths that must keep redirecting for previously downloaded PDFs. */
export const RETAINER_PDF_LEGACY_PATHS = ["/advocacy/apply"] as const;

/** Manual major/minor prefix — bump only for layout or structural redesigns. */
const SCHEMA_VERSION = "2";

/** Stable, order-insensitive 32-bit hash (FNV-1a) of the document payload. */
function contentHash(input: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36).padStart(7, "0").slice(-7);
}

const PAYLOAD = JSON.stringify({
  PREMIUM_TIERS,
  ALA_CARTE,
  COMPASSION_PLAN,
  SLIDING_SCALE_TIERS,
  BOUNDARIES,
  POSITIONING,
  cta: RETAINER_PDF_CTA_PATH,
  latest: RETAINER_PDF_LATEST_PATH,
});

/** e.g. "2.k3f9a1x" — changes automatically whenever PDF content changes. */
export const RETAINER_PDF_VERSION = `${SCHEMA_VERSION}.${contentHash(PAYLOAD)}`;
